import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import 'ol/ol.css'
import Map from 'ol/Map'
import TileLayer from 'ol/layer/Tile'
import View from 'ol/View'
import Zoomify from 'ol/source/Zoomify'

import pRetry from 'p-retry'

const getXmlData = (url) => {
  return fetch(url, {
    method: 'GET',
    mode: 'cors',
  })
    .then((res) => res.text())
    .then((text) => {
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(text, 'text/xml')
      const imageProperties = xmlDoc.documentElement
      return {
        width: imageProperties.getAttribute('WIDTH'),
        height: imageProperties.getAttribute('HEIGHT'),
      }
    })
}

const xmlDataFallback = { width: 0, height: 0 }

const ZoomImage = ({ className = '', tileSource }) => {
  const imageRef = useRef()

  const [xmlData, setXmlData] = useState(xmlDataFallback)

  useEffect(() => {
    const fn = () => getXmlData(`${tileSource}/ImageProperties.xml`)

    pRetry(fn, { retries: 3 })
      .then(setXmlData)
      .catch(() => {
        console.warn('Unable to fetch ImageProperties.xml')
        setXmlData(xmlDataFallback)
      })
  }, [tileSource])

  useEffect(() => {
    const el = imageRef.current

    const { width, height } = xmlData

    const source = new Zoomify({
      url: tileSource + '/',
      size: [width, height],
      crossOrigin: 'anonymous',
      zDirection: -1, // Ensure we get a tile with the screen resolution or higher
    })
    const extent = source.getTileGrid().getExtent()

    const layer = new TileLayer({
      source: source,
    })

    const view = new View({
      // adjust zoom levels to those provided by the source
      resolutions: layer.getSource().getTileGrid().getResolutions(),
      // constrain the center: center cannot be set outside this extent
      extent: extent,
      constrainOnlyCenter: true,
    })

    const map = new Map({
      layers: [layer],
      view: view,
    })

    map.setTarget(el)

    view.fit(extent)

    function getMinZoom() {
      const width = el.clientWidth
      const height = el.clientHeight

      return Math.min(
        Math.ceil(Math.LOG2E * Math.log(width / 256)),
        Math.ceil(Math.LOG2E * Math.log(height / 256))
      )
    }

    const setMinZoom = function () {
      const minZoom = getMinZoom()
      if (minZoom !== view.getMinZoom()) {
        view.setMinZoom(minZoom)
      }
    }

    setMinZoom()
    window.addEventListener('resize', setMinZoom)

    return () => {
      window.removeEventListener('resize', setMinZoom)
      map.setTarget(undefined)
    }
  }, [xmlData, tileSource])

  const child = (
    <div className={className + ' zoom-image'}>
      <div className="height-100" ref={imageRef} />
    </div>
  )

  return ReactDOM.createPortal(child, document.body)
}

export default ZoomImage
